/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { signatureLinkSanteMobiliteApi } from '@/api/santeMobiliteApi';
import { appError, appRedirect, appSuccess } from '@/redux/actions/appActions';
import { instance as axios } from '@/api/baseUrl';
import { trackDatadogAction, trackDatadogError } from '@/services/datadog';

const Spacing = styled.div`
  height: 400px;
  @media (max-width: 600px) {
    height: 200px;
  }
`;

/**
 * Page intermédiaire après le redirect Stripe pour le parcours payant Santé
 * Mobilité. Calquée sur Habitation SuccessPaymentPage :
 *
 * 1. Stripe redirige ici avec contractReference (en query param)
 * 2. On affiche le loader Studcorp full-screen (appRedirect)
 * 3. Si requiresSignature === true (zones 1/2/3) → appel /signature-link, redirect SellAndSign
 * 4. Si requiresSignature === false (zone 4) → redirect direct vers /success
 *
 * Cette page ne contient pas la logique SSE / attestation / merci : elle se
 * contente d'orienter l'utilisateur vers la suite (SellAndSign ou success
 * direct). Le post-signature atterrit sur la page /success qui gère le merci
 * + le DL attestation + la SSE de validation email pour les fast registered.
 */
/**
 * Budget d'attente de la signature : tentatives espacées de
 * SIGNATURE_RETRY_DELAY_MS.
 *
 * L'estimation d'origine — « le back peut prendre 1-2s » — était fausse d'un
 * facteur cinq. Le webhook Stripe génère l'attestation, l'envoie par mail,
 * notifie l'admin PUIS crée le contrat SellAndSign : mesuré à 9 018 ms sur
 * preprod (`[HM-WEBHOOK] Certificate + emails sent`, champ totalDurationMs).
 *
 * Avec 3 tentatives à 2 s, le front abandonnait au bout de ~6 s — trois
 * secondes AVANT que le back ait fini. L'utilisateur tombait sur l'écran
 * « Signature non disponible » alors que sa signature venait d'être créée,
 * y compris quand tout fonctionnait.
 *
 * 15 × 2 s = 30 s de budget, trois fois la durée observée. Généreux
 * volontairement : dépasser le budget mène l'utilisateur dans un cul-de-sac,
 * alors qu'attendre quelques secondes de plus ne coûte qu'un écran d'attente.
 */
const SIGNATURE_MAX_ATTEMPTS = 15;
const SIGNATURE_RETRY_DELAY_MS = 2000;

const SanteMobiliteSuccessPaymentPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const reduxContractReference = useAppSelector(
    state => state.santeMobilite.contractReference,
  );
  const contractReference =
    reduxContractReference ||
    localStorage.getItem('sante_mobilite_contractReference');

  const reduxRequiresSignature = useAppSelector(
    state => state.santeMobilite.requiresSignature,
  );
  const requiresSignature = (() => {
    if (typeof reduxRequiresSignature === 'boolean') return reduxRequiresSignature;
    const stored = localStorage.getItem('sante_mobilite_requiresSignature');
    if (stored === '1') return true;
    if (stored === '0') return false;
    return false;
  })();

  const calledRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Récupère le lien SellAndSign avec retry sur 409, le temps que le webhook
   * Stripe ait persisté la signature.
   */
  const fetchSignatureAndRedirect = async (ref: string, attempt = 0) => {
    if (!isMountedRef.current) return;
    try {
      const res = await signatureLinkSanteMobiliteApi(ref);
      if (!isMountedRef.current) return;
      const url: string | undefined = res.data?.signatureUrl;
      if (!url) throw new Error('signatureUrl missing in response');
      trackDatadogAction('sante_mobilite_signature_link_success', {
        contractReference: ref,
        attempt,
        signatureUrlHost: (() => {
          try {
            return new URL(url).host;
          } catch {
            return 'invalid_url';
          }
        })(),
      });
      localStorage.removeItem('sante_mobilite_requiresSignature');
      trackDatadogAction('sante_mobilite_signature_redirect', {
        contractReference: ref,
      });
      window.location.href = url;
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409 && attempt < SIGNATURE_MAX_ATTEMPTS) {
        trackDatadogAction('sante_mobilite_signature_link_409_retry', {
          contractReference: ref,
          attempt,
        });
        setTimeout(
          () => fetchSignatureAndRedirect(ref, attempt + 1),
          SIGNATURE_RETRY_DELAY_MS,
        );
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Erreur récupération signature SellAndSign:', err);
      trackDatadogError(err as Error, {
        action: 'sante_mobilite_signature_link_failed',
        contractReference: ref,
        status,
        attempt,
      });
      dispatch(appError());
      // Fallback : on envoie l'utilisateur sur la page success finale qui
      // affichera un message d'erreur dédié + le DL attestation.
      window.location.href = `/insurance/health-mobility/success?contractReference=${encodeURIComponent(
        ref,
      )}&signatureError=1`;
    }
  };

  useEffect(() => {
    if (!contractReference || calledRef.current) return;
    calledRef.current = true;
    dispatch(appRedirect());

    // Event clé : on note exactement avec quel state on arrive sur la page
    // post-Stripe. Si plus tard on n'a pas vu un signature_link_success, on
    // pourra remonter dans Datadog pour voir si la page a même été montée
    // et avec quel requiresSignature elle a été montée.
    trackDatadogAction('sante_mobilite_post_stripe_redirect', {
      contractReference,
      requiresSignature,
      requiresSignatureSource: (() => {
        if (typeof reduxRequiresSignature === 'boolean') return 'redux';
        const stored = localStorage.getItem('sante_mobilite_requiresSignature');
        if (stored === '1' || stored === '0') return 'localStorage';
        return 'fallback_false';
      })(),
    });

    // Petit délai pour laisser les cookies se stabiliser après le redirect
    // Stripe (même pattern que Habitation).
    const timer = setTimeout(async () => {
      if (!isMountedRef.current) return;

      try {
        await axios.post('/security/token/refresh');
        trackDatadogAction('sante_mobilite_token_refresh_success', {
          contractReference,
        });
      } catch (refreshError) {
        // L'interceptor gérera le retry au besoin.
        trackDatadogError(refreshError as Error, {
          action: 'sante_mobilite_token_refresh_failed',
          contractReference,
        });
      }
      if (!isMountedRef.current) return;

      if (requiresSignature) {
        fetchSignatureAndRedirect(contractReference);
      } else {
        // Zone 4 : pas de signature, on enchaîne sur la page success finale.
        trackDatadogAction('sante_mobilite_post_stripe_skip_signature', {
          contractReference,
        });
        dispatch(appSuccess());
        window.location.href = `/insurance/health-mobility/success?contractReference=${encodeURIComponent(
          contractReference,
        )}`;
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractReference, requiresSignature, dispatch]);

  return <Spacing />;
};

export default SanteMobiliteSuccessPaymentPage;
